import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import {
  Image as ImageIcon, Mic, Square, X, Send, Loader2,
  MessageCircle, Trash2, Pencil
} from 'lucide-react';

const API_URL = window.location.origin;

const REACTIONS = {
  love: { emoji: '❤️', label: 'Love' },
  celebrate: { emoji: '🎉', label: 'Celebrate' },
  pray: { emoji: '🙏', label: 'Praying' },
  strong: { emoji: '💪', label: 'Strength' },
  sparkle: { emoji: '✨', label: 'Sparkle' }
};

const mediaUrl = (url) => (url?.startsWith('http') ? url : `${API_URL}${url}`);

export const PostCard = ({ post, user, channel, onRefresh }) => {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const myReaction = Object.keys(REACTIONS).find(r => (post.reactions?.[r] || []).includes(user?.id));
  const totalReactions = Object.values(post.reactions || {}).reduce((s, arr) => s + (arr?.length || 0), 0);

  const handleReact = async (type) => {
    setShowPicker(false);
    try {
      await axios.post(`${API_URL}/api/legacy/posts/${post.id}/react`, { reaction_type: type });
      onRefresh();
    } catch {
      toast.error('Failed to react');
    }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    try {
      await axios.post(`${API_URL}/api/legacy/posts/${post.id}/comments`, { content: commentText });
      setCommentText('');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to comment');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API_URL}/api/legacy/posts/${post.id}`);
      toast.success('Post deleted');
      onRefresh();
    } catch {
      toast.error('Failed to delete post');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await axios.delete(`${API_URL}/api/legacy/posts/${post.id}/comments/${commentId}`);
      onRefresh();
    } catch {
      toast.error('Failed to delete comment');
    }
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API_URL}/api/legacy/posts/${post.id}`, { content: editText });
      setEditing(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update post');
    }
  };

  return (
    <Card className="card-base" data-testid={`legacy-post-${post.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={post.author_avatar ? mediaUrl(post.author_avatar) : undefined} />
              <AvatarFallback className="bg-forest text-white">{post.author_name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-forest">{post.author_name}</p>
              <p className="text-xs text-gray-400">
                {new Date(post.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                {post.edited_at && <span className="italic"> · edited</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {post.author_id === user?.id && !editing && (
              <button onClick={() => { setEditing(true); setEditText(post.content || ''); }} className="text-gray-300 hover:text-forest" data-testid={`legacy-post-edit-${post.id}`}>
                <Pencil className="h-4 w-4" />
              </button>
            )}
            {(post.author_id === user?.id || isAdmin) && (
              <button onClick={handleDelete} className="text-gray-300 hover:text-red-400" data-testid={`legacy-post-delete-${post.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {editing ? (
          <div className="mt-3 space-y-2">
            <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} data-testid={`legacy-post-edit-input-${post.id}`} />
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="sm" className="bg-forest text-white" onClick={handleSaveEdit} data-testid={`legacy-post-edit-save-${post.id}`}>Save</Button>
            </div>
          </div>
        ) : (
          post.content && <p className="text-gray-700 mt-3 whitespace-pre-wrap">{post.content}</p>
        )}

        {(post.media || []).map((m, i) => (
          <div key={i} className="mt-3">
            {m.type === 'image' && <img src={mediaUrl(m.url)} alt="" className="rounded-xl max-h-96 object-cover" />}
            {m.type === 'audio' && <audio controls src={mediaUrl(m.url)} className="w-full" data-testid={`legacy-post-audio-${post.id}`} />}
            {m.type === 'video' && <video controls src={mediaUrl(m.url)} className="rounded-xl max-h-96 w-full" />}
          </div>
        ))}

        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-sand">
          <div className="relative">
            <button
              onClick={() => setShowPicker(!showPicker)}
              className={`flex items-center gap-1 text-sm ${myReaction ? '' : 'text-gray-400 hover:text-forest'}`}
              data-testid={`legacy-react-btn-${post.id}`}
            >
              <span className="text-base">{myReaction ? REACTIONS[myReaction].emoji : '🤍'}</span>
              {totalReactions > 0 && <span className="text-gray-500">{totalReactions}</span>}
            </button>
            {showPicker && (
              <div className="absolute left-0 bottom-full mb-1 bg-white rounded-full shadow-lg border p-2 flex gap-1 z-10">
                {Object.entries(REACTIONS).map(([type, { emoji, label }]) => (
                  <button key={type} onClick={() => handleReact(type)} title={label}
                    className={`text-xl hover:scale-125 transition-transform p-1 rounded ${myReaction === type ? 'bg-rose-100' : ''}`}
                    data-testid={`legacy-reaction-${type}-${post.id}`}>
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => setShowComments(!showComments)} className="flex items-center gap-1 text-sm text-gray-400 hover:text-forest" data-testid={`legacy-comments-toggle-${post.id}`}>
            <MessageCircle className="h-4 w-4" /> {(post.comments || []).length}
          </button>
        </div>

        {showComments && (
          <div className="mt-3 space-y-3">
            {(post.comments || []).map(c => (
              <div key={c.id} className="flex gap-2 group">
                <Avatar className="h-7 w-7">
                  <AvatarImage src={c.author_avatar ? mediaUrl(c.author_avatar) : undefined} />
                  <AvatarFallback className="bg-forest text-white text-xs">{c.author_name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="bg-sand/50 rounded-xl px-3 py-2 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-medium text-forest">{c.author_name}</p>
                    {(c.author_id === user?.id || isAdmin) && (
                      <button onClick={() => handleDeleteComment(c.id)} className="text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`legacy-comment-delete-${c.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-gray-700">{c.content}</p>
                </div>
              </div>
            ))}
            <div className="flex gap-2">
              <Input value={commentText} onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleComment()}
                placeholder="Add an encouraging word..." className="text-sm"
                data-testid={`legacy-comment-input-${post.id}`} />
              <Button size="sm" onClick={handleComment} disabled={!commentText.trim()} className="bg-forest hover:bg-forest/90 text-white" data-testid={`legacy-comment-submit-${post.id}`}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const LegacyChannel = ({ channel, user, accent = 'text-terracotta', placeholder }) => {
  const [data, setData] = useState(null);
  const [content, setContent] = useState('');
  const [attachedImage, setAttachedImage] = useState(null);
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { isRecording, audioBlob, audioUrl, formattedDuration, startRecording, stopRecording, clearRecording } = useAudioRecorder();

  const fetchChannel = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/legacy/channels/${channel}`);
      setData(res.data);
    } catch (error) {
      console.error(`Failed to load ${channel}:`, error);
    }
  };

  useEffect(() => { fetchChannel(); }, [channel]);

  const uploadFile = async (file, filename) => {
    const form = new FormData();
    form.append('file', file, filename);
    const res = await axios.post(`${API_URL}/api/legacy/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data;
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const uploaded = await uploadFile(file, file.name);
      setAttachedImage(uploaded);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handlePost = async () => {
    if (!content.trim() && !attachedImage && !audioBlob) {
      toast.error('Share something — words, an image, or a voice note');
      return;
    }
    setPosting(true);
    try {
      const media = [];
      if (attachedImage) media.push({ type: attachedImage.type, url: attachedImage.url });
      if (audioBlob) {
        const uploaded = await uploadFile(audioBlob, 'voice-note.webm');
        media.push({ type: 'audio', url: uploaded.url });
      }
      await axios.post(`${API_URL}/api/legacy/channels/${channel}/posts`, { content, media });
      toast.success('Shared with the circle');
      setContent('');
      setAttachedImage(null);
      clearRecording();
      fetchChannel();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  if (!data) {
    return <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-sand rounded-xl animate-pulse" />)}</div>;
  }

  const prompt = data.current_prompt || {};

  return (
    <div className="space-y-6" data-testid={`legacy-channel-${channel}`}>
      {/* Current prompt */}
      <Card className="card-base border-terracotta/20 bg-gradient-to-br from-terracotta/5 to-transparent">
        <CardContent className="p-6">
          <p className={`text-xs uppercase tracking-widest ${accent} mb-2`}>{data.title}</p>
          <p className="font-heading text-xl text-forest" data-testid={`legacy-prompt-${channel}`}>{prompt.text}</p>
        </CardContent>
      </Card>

      {/* Composer */}
      <Card className="card-base">
        <CardContent className="p-4 space-y-3">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={placeholder}
            rows={3}
            className="border-sand"
            data-testid={`legacy-composer-${channel}`}
          />
          {attachedImage && (
            <div className="relative inline-block">
              <img src={mediaUrl(attachedImage.url)} alt="" className="h-24 rounded-xl object-cover" />
              <button onClick={() => setAttachedImage(null)} className="absolute -top-2 -right-2 bg-white rounded-full shadow p-1">
                <X className="h-3 w-3 text-gray-600" />
              </button>
            </div>
          )}
          {(isRecording || audioUrl) && (
            <div className="flex items-center gap-3 bg-sand/50 rounded-xl p-3">
              {isRecording ? (
                <>
                  <span className="flex items-center gap-2 text-sm text-red-500 font-medium">
                    <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /> Recording {formattedDuration}
                  </span>
                  <Button size="sm" variant="outline" onClick={stopRecording} data-testid={`legacy-stop-recording-${channel}`}>
                    <Square className="h-3 w-3 mr-1" /> Stop
                  </Button>
                </>
              ) : (
                <>
                  <audio controls src={audioUrl} className="h-9 flex-1" />
                  <button onClick={clearRecording} className="text-gray-400 hover:text-red-400"><X className="h-4 w-4" /></button>
                </>
              )}
            </div>
          )}
          <div className="flex items-center gap-2">
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid={`legacy-attach-image-${channel}`}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
            </Button>
            {!isRecording && !audioUrl && (
              <Button variant="outline" size="sm" onClick={startRecording} data-testid={`legacy-record-voice-${channel}`}>
                <Mic className="h-4 w-4" />
              </Button>
            )}
            <Button onClick={handlePost} disabled={posting || isRecording} className="bg-forest hover:bg-forest/90 text-white ml-auto" data-testid={`legacy-post-submit-${channel}`}>
              {posting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />} Share
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Feed */}
      {data.posts.length > 0 ? (
        <div className="space-y-4">
          {data.posts.map(post => (
            <PostCard key={post.id} post={post} user={user} channel={channel} onRefresh={fetchChannel} />
          ))}
        </div>
      ) : (
        <p className="text-center text-sm text-gray-400 py-6">Be the first to share this week.</p>
      )}
    </div>
  );
};

export default LegacyChannel;
